/**
 * globalFilters.jsx · v2.8.9.6
 * ─────────────────────────────
 * Context de filtros globales persistentes en localStorage.
 * Se comparte entre Kanban, Lista, Integración Odoo y Database Viewer.
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api from "./api";
import { PUBLIC_PATHS } from "./api";
import { useAuth } from "./auth";

const STORAGE_KEY = "afc.global_filters.v320";

// v3.2.0 · Filtros globales soportan MULTI-select (arrays) además de single (string/null).
// Backend acepta CSV en cualquiera de estos params.
const DEFAULT_FILTERS = {
  branch_office_id: [],       // v3.2.0 multi
  zone_id: [],                // v3.2.0 multi
  technician_id: [],          // v3.2.0 multi
  odoo_stage_name: [],        // v3.2.0 multi
  internal_status: null,
  q: "",
  cpe: "",                    // v9.4.0 · Búsqueda CPE/Serie
  has_gps: "",                // v9.4.0 · Filtro GPS ("", "true", "false")
  created_from: null,
  created_to: null,
  scheduled_from: null,
  scheduled_to: null,
  stage_mismatch: null,
  no_activity: false,         // v97.0.0 · Solo OT sin tipo de actividad
  solution_node_id: [],       // v101.227.0 · Tipos de solución (incluye hijos)
  contratista: [],            // v101.227.0 · Contratistas (de la OT o del técnico)
};

const GlobalFiltersContext = createContext(null);

export function GlobalFiltersProvider({ children }) {
  const [filters, setFilters] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT_FILTERS, ...JSON.parse(raw) };
    } catch (_) { /* silencioso */ }
    return DEFAULT_FILTERS;
  });

  const [options, setOptions] = useState({
    branches: [], zones: [], technicians: [], odoo_stages: [], internal_statuses: [],
    solution_types: [], contratistas: [],   // v101.227.0
    loading: true,
  });

  // Persistir cada vez que cambian
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(filters)); } catch (_) { /* silencioso */ }
  }, [filters]);

  // v94.0.0 · Recargar las opciones cuando cambia el usuario (al iniciar sesión, cerrar sesión o cambiar de usuario).
  //           Antes se cargaban una sola vez al abrir la app: en la pantalla de login no hay token, así que
  //           los filtros quedaban vacíos hasta recargar la página.
  const { user: _authUser } = useAuth();
  const _uid = _authUser?.id || _authUser?.email || null;

  // Cargar opciones — solo en rutas protegidas con token
  useEffect(() => {
    if (!_uid) {
      setOptions({ branches: [], zones: [], technicians: [], odoo_stages: [], internal_statuses: [], loading: false });
      return;
    }
    const token = localStorage.getItem("afc_token");
    const isPublic = PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));
    if (!token || isPublic) {
      setOptions((o) => ({ ...o, loading: false }));
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/v1/ot_smart/filter-options");
        setOptions({ ...data, loading: false });
      } catch (_) {
        setOptions((o) => ({ ...o, loading: false }));
      }
    })();
  }, [_uid]);

  const setFilter = useCallback((k, v) => {
    setFilters((f) => ({ ...f, [k]: v || null }));
  }, []);

  const setMany = useCallback((patch) => {
    setFilters((f) => ({ ...f, ...patch }));
  }, []);

  const reset = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const activeCount = useMemo(() => {
    return Object.entries(filters).filter(([k, v]) => {
      if (v === null || v === undefined || v === "" || v === false) return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    }).length;
  }, [filters]);

  // Params serializados para consumir en cualquier endpoint compatible
  // v3.2.0 · Arrays se envían como CSV (backend acepta ambos: single o CSV).
  const _csv = (v) => Array.isArray(v) ? (v.length ? v.join(",") : null) : (v || null);
  const asParams = useMemo(() => {
    const p = {};
    const b = _csv(filters.branch_office_id); if (b) p.branch_office_id = b;
    const z = _csv(filters.zone_id); if (z) p.zone_id = z;
    const t = _csv(filters.technician_id); if (t) p.technician_id = t;
    const os = _csv(filters.odoo_stage_name); if (os) p.odoo_stage_name = os;
    if (filters.internal_status) p.internal_status = filters.internal_status;
    if (filters.q) p.q = filters.q;
    if (filters.cpe) p.cpe = filters.cpe;
    if (filters.has_gps === "true" || filters.has_gps === "false") p.has_gps = filters.has_gps;
    if (filters.created_from) p.created_from = filters.created_from;
    if (filters.created_to) p.created_to = filters.created_to;
    if (filters.scheduled_from) p.scheduled_from = filters.scheduled_from;
    if (filters.scheduled_to) p.scheduled_to = filters.scheduled_to;
    if (filters.stage_mismatch) p.stage_mismatch = true;
    if (filters.no_activity) p.no_activity = true;
    const sn = _csv(filters.solution_node_id); if (sn) p.solution_node_id = sn;   // v101.227.0
    const ct = Array.isArray(filters.contratista) ? filters.contratista : (filters.contratista ? [filters.contratista] : []);
    if (ct.length) p.contratista = ct.join("|");   // nombres con coma: separador «|»
    return p;
  }, [filters]);

  return (
    <GlobalFiltersContext.Provider value={{ filters, options, setFilter, setMany, reset, activeCount, asParams }}>
      {children}
    </GlobalFiltersContext.Provider>
  );
}

export function useGlobalFilters() {
  const ctx = useContext(GlobalFiltersContext);
  if (!ctx) throw new Error("useGlobalFilters must be used inside GlobalFiltersProvider");
  return ctx;
}
